/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import {
  RankedTester,
  rankWith,
  UISchemaElement,
  uiTypeIs,
  RendererProps,
} from "@jsonforms/core";
import { ListBox, Section, Item, Text } from "@adobe/react-spectrum";
import { useJsonForms, withJsonFormsLayoutProps } from "@jsonforms/react";
import React, { Suspense, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Lodash
import { get } from "lodash";

// Redux
import { useSelector } from "react-redux";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";

import styles from "./css/LeftMenuRenderer.module.scss";
import { AllIcons } from "../utils/icons";

type menuListObj = {
  name: string;
  redirect?: {
    type: string;
    path: string;
  };
  iconProps?: { iconName: string; spectrumProps?: object };
  showAlert?: any;
};

type optionsType = {
  menuTitle?: string;
  menuList: menuListObj[];
};

export const leftMenuRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("LeftMenu")
);
function LeftMenuRenderer({ uischema, visible }: RendererProps) {
  const optionsSchema = uischema as UISchemaElement;
  const { menuTitle: sectionTitle, menuList } =
    (optionsSchema.options as optionsType) || {};
  const navigate = useNavigate();
  const { config: jsonFormConfig } = useJsonForms();
  const pathName = useLocation();
  const onSuccessCallbacks = jsonFormConfig?.lifecycleHooks?.onSuccessCallbacks;

  const alertIconHandler = onSuccessCallbacks?.alertIconHandler;
  const data = jsonFormConfig?.dynamicData?.data || {};

  /** method to set alert icons in menu -callback */
  const setAlertIcon = (index: string | number, bool: any) => {
    // console.log(index);
    if (menuList.length && menuList[index]) {
      menuList[index].showAlert = bool;
    }
    // console.log(menuList);
  };

  useEffect(() => {
    if (alertIconHandler) {
      alertIconHandler({ setAlertIcon });
    }
  }, []);

  /**  method to replace variables from params  */
  const replaceUrl = (redirectObject: { type: string; path: string }) => {
    let redirectPath = redirectObject.path;
    // console.log(data);
    if (data) {
      const regex = new RegExp(`\\\${(${Object.keys(data).join("|")})}`, "g");
      // console.log(regex);
      redirectPath = redirectPath.replace(
        regex,
        (matchString, $1) => data[$1] || matchString
      );
      // console.log(redirectPath);
    }

    return redirectPath;
  };

  const setLabel = (redirectObject: { type: string; path: string }) => {
    const routerLink = redirectObject?.path ? replaceUrl(redirectObject) : "";
    return routerLink && pathName.pathname === routerLink
      ? "activeItemLbl"
      : "";
  };
  /**  method to handle menu click  */
  const onNavAction = (menuSelected: any) => {
    const selectedMenu = menuList.find(
      (menu: { name: string }) =>
        menu.name.toLowerCase().replace(/ /g, "_") === menuSelected.currentKey
    );
    if (selectedMenu && selectedMenu.redirect) {
      navigate(replaceUrl(selectedMenu.redirect));
    }
  };
  /**  renderIcon method to add icons in menu name */
  const renderIcon = (
    iconName: string,
    iconSpectrumProps: object,
    style: any
  ) => {
    let iconJsx = null;
    if (Object.keys(AllIcons).includes(iconName)) {
      const Icon = AllIcons[iconName];

      iconJsx = (
        <Suspense fallback={<div>Page is Loading...</div>}>
          <Icon {...iconSpectrumProps} UNSAFE_style={style} />
        </Suspense>
      );
    }
    return iconJsx;
  };

  // Determine if the component should be visible
  let componentVisibility = visible;

  // If there is a context rule, evaluate it and use that visibility rule instead.
  if (uischema.options?.contextRule) {
    // grab the scope from the contextRule
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    // get the redux store state based on the scope provided
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    componentVisibility = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    )?.visible;
  }

  return (
    <div
      data-testid="custom-left-menu"
      className={styles.leftMenuContainer}
      hidden={!componentVisibility}
    >
      {menuList && (
        <ListBox
          data-testid="custom-list-box"
          aria-label="Pick menu"
          items={menuList}
          selectedKeys={new Set(["menu"])}
          selectionMode="single"
          onSelectionChange={onNavAction}
          UNSAFE_className={styles.leftMenuBox}
        >
          <Section
            title={sectionTitle || ""}
            items={menuList}
            data-testid="custom-list-section"
          >
            {menuList.map((subItem: any) => (
              <Item
                key={subItem.name.toLowerCase().replace(/ /g, "_")}
                textValue={subItem.name}
                aria-label={
                  subItem.redirect ? setLabel(subItem.redirect) : "item"
                }
              >
                <Text UNSAFE_className={`${styles.leftMenuTitle}`}>
                  {subItem?.iconProps &&
                    subItem?.iconProps?.iconName &&
                    renderIcon(
                      subItem?.iconProps?.iconName,
                      subItem?.iconProps?.spectrumProps || {},
                      subItem?.iconProps?.style
                    )}
                  {subItem.name}
                </Text>
                {subItem.showAlert && (
                  <Text UNSAFE_className={styles.alertIcon}>
                    {renderIcon("Alert", { color: "negative" }, {})}
                  </Text>
                )}
                {subItem.children && (
                  <ul
                    key={`contentList_${subItem.name}`}
                    data-testid="custom-sub-menu"
                    className={styles.menuUList}
                  >
                    {subItem.children.map(
                      (itemObj: {
                        name:
                          | boolean
                          | React.ReactChild
                          | React.ReactFragment
                          | React.ReactPortal
                          | null
                          | undefined;
                      }) => (
                        <li
                          key={`left_${itemObj.name}`}
                          className={styles.liCls}
                        >
                          {itemObj.name}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </Item>
            ))}
          </Section>
        </ListBox>
      )}
    </div>
  );
}
export default withJsonFormsLayoutProps(LeftMenuRenderer);
